import React from 'react';
import { COLORS, SHADOWS } from '../../../styles/UISystem';
import { CaretLeft } from '@phosphor-icons/react/dist/ssr/CaretLeft';
import { IconCircleButton } from '../Icons/IconCircleButton';

/**
 * HeaderProfile Component
 *
 * A header component for profile detail screens with frosted glass design.
 * Matches the design of HeaderConnectionImage for consistency.
 *
 * @param {string} name - Full name to display in the pill
 * @param {string} imageUrl - URL of the profile image
 * @param {string} initialLetter - Initial letter to display when no image
 * @param {function} onBack - Callback function for back button
 * @param {function} onImageClick - Optional callback function when profile image is clicked
 */
export const HeaderProfile = ({
  name = '',
  imageUrl = '',
  initialLetter = '',
  onBack,
  onImageClick,
  rightAction,
}) => {
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.85)', // Semi-transparent white for frosted glass effect
        backdropFilter: 'blur(20px)', // iOS-style blur
        WebkitBackdropFilter: 'blur(20px)', // Safari support
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '4px',
        paddingBottom: '6px',
      }}
    >
      {/* Top row: Back button and Profile image */}
      <div
        style={{
          width: '100%',
          height: '44px', // Fixed height matching ConnectionImage
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: '16px',
          paddingRight: '16px',
          marginBottom: '2px',
        }}
      >
        {/* Back button in white circle - left side */}
        {onBack && (
          <div style={{ position: 'absolute', left: '16px' }}>
            <IconCircleButton
              icon={CaretLeft}
              size={20}
              buttonSize={44}
              iconWeight="bold"
              onClick={onBack}
            />
          </div>
        )}

        {/* Right action slot - mirrors back button placement */}
        {rightAction && <div style={{ position: 'absolute', right: '16px' }}>{rightAction}</div>}

        {/* Profile image - center (absolutely positioned) */}
        <div
          onClick={onImageClick}
          style={{
            cursor: onImageClick ? 'pointer' : 'default',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: 0,
          }}
        >
          <div
            style={{
              width: '80px', // Larger than ConnectionImage (60px)
              height: '80px',
              borderRadius: '50%',
              backgroundColor: COLORS.MEDIUM_GRAY,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : initialLetter ? (
              <div
                style={{
                  fontSize: '36px', // Larger font for bigger circle
                  fontWeight: 'bold',
                  color: COLORS.WHITE,
                }}
              >
                {initialLetter}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Name pill - centered below profile image */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '28px', // More space for larger image (80px/2 - 44px/2 + 10px base)
        }}
      >
        {name && name.trim() !== '' ? (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '18px',
              padding: '6px 12px',
              cursor: onImageClick ? 'pointer' : 'default',
              boxShadow: SHADOWS.TIER1,
            }}
            onClick={onImageClick}
          >
            <span
              style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#000000',
                letterSpacing: '-0.3px',
              }}
            >
              {name}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/**
 * HeaderProfileExample Component
 *
 * Example usage of the HeaderProfile component with different variants.
 */
export const HeaderProfileExample = () => {
  const handleBack = () => {
    console.log('Back button clicked');
  };

  return (
    <div style={{ marginBottom: '60px' }}>
      <div
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: COLORS.DARK_GRAY,
          marginBottom: '32px',
          textAlign: 'center',
        }}
      >
        HeaderProfile
      </div>

      {/* Blank example */}
      <div style={{ marginBottom: '32px' }}>
        <div
          style={{
            marginBottom: '8px',
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            textAlign: 'center',
          }}
        >
          blank
        </div>
        <HeaderProfile variant="blank" onBack={handleBack} />
      </div>

      {/* Full example */}
      <div style={{ marginBottom: '32px' }}>
        <div
          style={{
            marginBottom: '8px',
            fontSize: '16px',
            color: COLORS.DARK_GRAY,
            textAlign: 'center',
          }}
        >
          full
        </div>
        <HeaderProfile
          variant="full"
          imageUrl="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=256&q=80"
          onBack={handleBack}
        />
      </div>
    </div>
  );
};
